import { CircularProgress, VeBox, VeTypography } from "@packages/ui/mui";
import { useState } from "react";
import { boolean } from "zod";
import { toaster } from "../toaster/toast";

interface FileInfo {
  s3ObjectKey: string;
  fileSize: number;
  status: "ACTIVE" | "IN_ACTIVE";
  originalFileName: string;
  commentStatus?: string;
  comment?: string;
  objectKey?: string;
  docType?: string;
  idDocSubType?: string;
}

const Upload = ({
  uploadFile,
  setUpload,
  acceptTypes = [],
  bucketType,
  multiple = false,
  maxSize,
  useSession,
  uploadFiles,
  icon,
  index,
  highlightError = false,
  image,
  fileTypeImages,
  setFileUploading,
  docType,
  idDocSubType = "",
  smallIcon = false,
  belowTitle,
  sizeRequired = true,
  noMargin = false,
  kmlFileType = false,
  errorText = "",
  cmsUpload = false,
  noPermission = false,
}: {
  uploadFile?: any;
  setUpload: any;
  acceptTypes?: string[];
  bucketType: any;
  multiple?: boolean;
  maxSize?: number;
  useSession?: any;
  uploadFiles?: any;
  icon?: any;
  index: any;
  highlightError?: boolean;
  fileTypeImages?: any;
  image?: any;
  setFileUploading?: any;
  docType?: string;
  idDocSubType?: string;
  smallIcon?: boolean;
  belowTitle?: string;
  sizeRequired?: boolean;
  noMargin?: boolean;
  kmlFileType?: boolean;
  errorText?: string;
  cmsUpload?: boolean;
  noPermission?: boolean;
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const { data: session, update } = useSession();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) {
      return;
    }
    const newSelectedFiles = Array.from(files);
    if (maxSize) {
      const validationErrors = newSelectedFiles
        .map((file) => {
          const fileType = file.type;
          const fileSize = file.size;
          const fileExtension = file.name
            .substring(file.name.lastIndexOf("."))
            .toLowerCase();
          // if (!kmlFileType && !acceptTypes.includes(fileType)) {
          //   if (errorText?.length > 0) {
          //     return errorText;
          //   } else return "Unsupported File Format. Please try again!";
          // }
          // if (
          //   (fileType?.length === 0 &&
          //     kmlFileType &&
          //     !acceptTypes.includes(fileExtension)) ||
          //   (fileType && kmlFileType && !acceptTypes.includes(fileType))
          // )
          //   return "Unsupported File Format. Please try again!";
          if (fileSize >= maxSize * 1024 * 1024) {
            return `Maximum file size could not be greater than ${maxSize} MB`;
          }
          return null; // No error for this file
        })
        .filter((msg) => msg !== null);
      if (validationErrors.length > 0) {
        // setFiles([]); // To prevent removing all the other valid files in the container
        // setUpload(null); // To prevent setting null to all the other valid files in the state
        setIsLoading(false);
        setError(validationErrors.join(". ")); // Combine all errors into a single message
        return;
      }
    }

    if (files && files.length > 0) {
      setIsLoading(true);
      if (setFileUploading) {
        setFileUploading(true);
      }
    }
    setError(null);

    try {
      const formData = new FormData();
      const fileInfos: FileInfo[] = [];
      Array.from(files).forEach((file: File) => {
        const pattern = /[^a-zA-Z0-9 _.-]/g;
        const fileName = file?.name.replace(pattern, "_");
        const newFile = new File([file], fileName, { type: file.type });
        formData.append("files", newFile);
        if (fileName && file?.size > 0) {
          fileInfos.push({
            s3ObjectKey: file?.name,
            fileSize: file?.size,
            status: "ACTIVE",
            originalFileName: file?.name,
            comment: "",
            commentStatus: "",
            objectKey: file?.name,
            docType: docType,
            ...((idDocSubType || typeof idDocSubType === "object") && {
              idDocSubType,
            }),
          });
        }
      });
      formData.append("bucket", bucketType);
      const data = await uploadFiles({
        data: formData,
        type: "post",
        endPoint: "/files/upload",
        session,
        headersReqWithSession: {
          "Content-Type": `multipart/form-data`,
        },
        update,
      });

      const updatedFileInfos = fileInfos?.map((item, index) => ({
        ...item,
        objectKey: data?.fileNames[index]?.objectKey,
        s3ObjectKey: data?.fileNames[index]?.objectKey,
      }));
      setIsLoading(false);
      if (setFileUploading) {
        setFileUploading(false);
      }

      if (multiple) {
        setUpload((prevState: FileInfo[]) => {
          const updatedState = [prevState, ...updatedFileInfos]
            .flat()
            .filter((item) => !(Array.isArray(item) && item.length === 0))
            .filter((item) => item !== undefined); // REMOVE IF UPLOAD BREAKS IN COMPANY PROFILE
          return updatedState;
        });
      } else {
        setUpload([updatedFileInfos[0]]);
      }

      if (!data?.error) {
        setFiles(e?.target?.files as any);
        setUploadError("");
        setError(null);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        setError("Something went wrong!.");
        setUploadError("Can't upload this file");
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setError("Something went wrong!");
      // toaster("Something went wrong!.", { type: "error" });
    }
  };

  const handleFileChangeForCms = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) return;

    const newSelectedFiles = Array.from(files);

    if (maxSize) {
      const validationErrors = newSelectedFiles
        .map((file) => {
          if (file.size >= maxSize * 1024 * 1024) {
            return `Maximum file size could not be greater than ${maxSize} MB`;
          }
          return null;
        })
        .filter((msg) => msg !== null);

      if (validationErrors.length > 0) {
        setIsLoading(false);
        setError(validationErrors.join(". "));
        return;
      }
    }

    if (files && files.length > 0) {
      setIsLoading(true);
      if (setFileUploading) {
        setFileUploading(true);
      }
    }
    setError(null);

    try {
      const formData = new FormData();
      newSelectedFiles.forEach((file: File) => {
        const pattern = /[^a-zA-Z0-9 _.-]/g;
        const fileName = file.name.replace(pattern, "_");
        formData.append("file", file);
        formData.append("name", fileName);
      });

      const data = await uploadFiles({
        data: formData,
        type: "post",
        endPoint: "/project/description/upload-files",
        session,
        headersReqWithSession: {
          "Content-Type": "multipart/form-data",
        },
        update,
      });

      setIsLoading(false);

      if (multiple) {
        setUpload((prev: FileInfo[]) => [...prev, data?.url?.id]);
      } else {
        setUpload([
          {
            id: data?.url?.id,
            name: data?.url?.name,
            url: data?.url?.url,
            fileSize: data?.url?.size,
          },
        ]);
      }

      if (!data?.error) {
        setFiles(files as any);
        setUploadError("");
        setError(null);
      } else {
        setError("Something went wrong!");
        setUploadError("Can't upload this file");
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setError("Something went wrong!");
    }
  };

  const handleUpload = () => {
    uploadFile({ files });
  };

  return (
    <VeBox>
      <VeBox
        marginTop={!noMargin ? "8px" : "0px"}
        borderRadius={"6px"}
        border={` 1px dashed ${highlightError ? "red" : !smallIcon && "#E8E8E8"
          }`}
        sx={{
          filter: noPermission ? "greyscale(100%)" : "none",
        }}
      >
        <VeBox height={!smallIcon ? "122px" : undefined}>
          <input
            type="file"
            id={`upload-${index}`}
            name={`upload-${index}`}
            multiple={multiple}
            hidden
            disabled={noPermission}
            onChange={cmsUpload ? handleFileChangeForCms : handleFileChange}
            accept={acceptTypes?.join(", ")}
          />
          <label
            htmlFor={`upload-${index}`}
            // className={styles.uploadLabel}
            style={{
              cursor: noPermission ? "auto" : "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {smallIcon ? (
              icon
            ) : (
              <VeBox
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                {/* <Image alt="upload_btn" src={UPLOAD_ICON} />
                 */}
                {icon}
                <VeTypography
                  variant="label3Brand"
                  marginTop={"12px"}
                  className={noPermission ? "" : "document_title"}
                >
                  {noPermission
                    ? "You do not have permission to upload"
                    : "Click to Upload"}
                </VeTypography>
                {!noPermission && <VeTypography variant="label4Tertiary" mt={0.5}>
                  {" "}
                  {sizeRequired && `(Max file size: ${maxSize} MB)`}
                </VeTypography>}  
                <VeTypography variant="label4Main">{belowTitle}</VeTypography>
              </VeBox>
            )}
          </label>
        </VeBox>
      </VeBox>
      {isLoading && (
        <VeBox display="flex" flexDirection="row" gap="8px" marginTop="6px">
          <CircularProgress sx={{ color: "var(--vc-violet)" }} size={10} />
          <VeBox display="flex" alignItems="center">
            <VeTypography variant="label4MainBrand">Uploading</VeTypography>
          </VeBox>
        </VeBox>
      )}
      {/* {error?.length && (
        <VeBox display="flex" flexDirection="row" marginTop="6px">
          <VeTypography variant="label3Error">{error}</VeTypography>
        </VeBox>
      )} */}
      {(error!?.length > 0 && error && !isLoading) ||
        (uploadError &&
          uploadError?.length > 0 &&
          !isLoading &&
          files?.length > 0) ? (
        <VeBox
          display="flex"
          justifyContent={"space-between"}
          alignContent={"center"}
          flexDirection="row"
          marginTop="6px"
        >
          <VeTypography variant="label3Error">
            {error ? error : uploadError}
          </VeTypography>
          <VeBox
            onClick={() => {
              setError(null);
            }}
            sx={{ cursor: "pointer" }}
            width={"9px"}
            height={"9px"}
            display={"flex"}
            alignItems={"center"}
          >
            {image(fileTypeImages?.close?.src, "close", 9, 9)}
          </VeBox>
        </VeBox>
      ) : undefined}
      {/* {files.length && !error && !uploadError ? (
        <VeBox display={"flex"} flexDirection={"column"}>
          <VeTypography variant="label2Main" style={{ padding: "16px 0" }}>
            {files.length} {files.length ? "Files" : "File"} selected
          </VeTypography>
        </VeBox>
      ) : (
        ""
      )} */}
    </VeBox>
  );
};

export default Upload;
